/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-ml */

import Component from '@glimmer/component';
import type FinCustom from 'embercom/models/operator/visual-builder/step/fin-custom';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type FinCustomConfiguration from 'embercom/objects/visual-builder/configuration/step/fin-custom';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { type OutputParam } from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import type InsertableAction from 'embercom/models/workflow-connector/insertable-action';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
import type { TaskStep } from '@intercom/interblocks.ts';
import { camelize } from '@ember/string';

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

interface Arguments {
  step: FinCustom;
  stepConfig: FinCustomConfiguration;
  editorState: EditorState;
  isReadOnly: boolean;
  onAddOutputParam: (outputParam: OutputParam) => AttributeDescriptor;
  onUpdateOutputParam: (outputParam: OutputParam) => void;
  onRemoveOutputParam: (outputParam: OutputParam) => void;
  onCopyActionOutputParam: (action: InsertableAction) => void;
  outputParams: OutputParam[];
}
function camelizeKeys(hash: object): object | null {
  if (!hash) {
    return null;
  }

  let entries = Object.entries(hash);
  let camelizedEntries = entries.map(([key, value]) => {
    let camelizedValue = value;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      camelizedValue = camelizeKeys(value);
    }
    return [camelize(key), camelizedValue];
  });
  return Object.fromEntries(camelizedEntries);
}

export default class Settings extends Component<Signature> {
  @service appService: any;
  @service attributeService: any;
  @service store: any;
  @service contentEditorService: any;
  @service modalService: any;

  @tracked openInserters: any;
  @tracked selectedTab = 'configure_section';
  @tracked allowMultiTurn = this.args.step.allowMultiTurn;
  @tracked allowContentSearch = this.args.step.allowContentSearch;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);

    // Initialize allowMultiTurn if not already set
    if (this.args.step.allowMultiTurn === undefined) {
      this.args.step.set('allowMultiTurn', true);
      this.allowMultiTurn = true;
    }
  }

  get app() {
    return this.appService.app;
  }

  get allowedAttributes() {
    let userAttributes: Array<{
      identifier: string;
      name: string;
      description: string;
      displayable: boolean;
      type: string;
    }> = this.attributeService.userAttributes;

    let companyAttributes: Array<{
      identifier: string;
      name: string;
      description: string;
      displayable: boolean;
      type: string;
    }> = this.attributeService.companyAttributes.map((attr: any) => attr.attribute); // don't know why this extra layer exists for company attrs

    let conversationAttributes: Array<{
      identifier: string;
      description: string;
      name: string;
      type: string;
    }> = this.attributeService.conversationAttributes;

    let temporaryAttributes: Array<{
      identifier: string;
      name: string;
      description: string;
      type: string;
    }> = this.args.step.editorConfig?.localAttributes ?? [];

    return {
      userAttributes: userAttributes
        .filter((attr) => attr.displayable)
        .map((attr) => ({
          identifier: attr.identifier,
          name: attr.name,
          description: attr.description,
          type: attr.type,
        })),
      companyAttributes: companyAttributes
        .filter((attr) => attr.displayable)
        .map((attr) => ({
          identifier: attr.identifier,
          name: attr.name,
          description: attr.description,
          type: attr.type,
        })),
      conversationAttributes: conversationAttributes.map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description,
        type: attr.type,
      })),
      temporaryAttributes: temporaryAttributes.map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description || '',
        type: attr.type,
      })),
    };
  }

  get canShowContentSearchToggle() {
    return this.app.canUseFeature('answerbot-fin-agentic-content-search');
  }

  get shouldShowAgenticSettings() {
    return this.canShowContentSearchToggle;
  }

  get shouldShowTestingSuiteButton() {
    return this.args.step.isV3 && this.app.canUseFeature('tasks-sophisticated-testing');
  }

  get shouldShowTestConfigPanel() {
    return (
      this.args.editorState.showTestConfigPanel &&
      this.args.step.isV3 &&
      this.app.canUseFeature('tasks-sophisticated-testing')
    );
  }

  get showSdkView() {
    // if the feature is not enabled, we never show the preview view
    if (!this.app.canUseFeature('team-actions-sdk-preview')) {
      return false;
    }

    // we only show the preview view if there is procedure content
    if (!this.args.step.finProcedureContent?.content?.length) {
      return false;
    }

    let content = this.args.step.finProcedureContent.content;

    // use the preview view if there is any non-taskSection sections
    if (content.some((section) => section.type !== 'taskStep')) {
      return true;
    }

    // use the preview view if there is any non instructionTaskStep steps
    if (content.some((section) => (section as TaskStep)?.content?.type !== 'instructionTaskStep')) {
      return true;
    }

    // At this point we have a procedure, but it is fully compatible with instructions.
    // We dont need to use the preview view in this case.
    return false;
  }

  @action
  onSelectionChange(sectionId: string) {
    this.selectedTab = sectionId;
  }

  @action
  toggleAllowMultiTurn() {
    this.allowMultiTurn = !this.allowMultiTurn;
    this.args.step.set('allowMultiTurn', this.allowMultiTurn);
  }

  @action
  toggleAllowContentSearch() {
    this.allowContentSearch = !this.allowContentSearch;
    this.args.step.set('allowContentSearch', this.allowContentSearch);
  }

  /**
   * Extracts escalation destination identifiers mentioned in instruction blocks
   */
  getMentionedEscalationDestinations(): Set<string> {
    let mentioned = new Set<string>();
    let instructions = this.args.step.instructions || [];

    // Extract template variables from all instruction blocks
    let templateVariables = instructions.flatMap((instruction) => {
      return extractTemplateVariables(instruction, true);
    });

    // Filter for assignee variables (escalation destinations)
    templateVariables.forEach((variable) => {
      if (variable.startsWith('assignee.')) {
        mentioned.add(variable);
      }
    });

    return mentioned;
  }

  get escalationDestinations() {
    let resolver = this.args.stepConfig.commandsResolver;
    let mentionedDestinations = this.getMentionedEscalationDestinations();
    // Filter helper - always include default, plus any mentioned destinations
    let filterDestinations = (destinations: any[], isDefaultTeam = false) => {
      let filtered = destinations.filter((attr: any) => {
        if (isDefaultTeam) {
          return true;
        }

        return mentionedDestinations.size !== 0 && mentionedDestinations.has(attr.identifier);
      });

      return filtered.map((attr: any) => ({
        id: attr.identifier.split('.')[1] ?? attr.identifier,
        name: attr.name,
        avatar: attr.object?.avatar ? camelizeKeys(attr.object.avatar) : undefined,
      }));
    };

    return {
      defaultTeam: {
        ...resolver.defaultTeam,
        attributes: filterDestinations(resolver.defaultTeam.attributes, true),
      },
      teams: {
        ...resolver.teams,
        attributes: filterDestinations(resolver.teams.attributes, false),
      },
      teammates: {
        ...resolver.teammates,
        attributes: filterDestinations(resolver.teammates.attributes, false),
      },
    };
  }

  @action
  openTestingSuite() {
    let modalProps = {
      open: true,
      workflowId: this.args.editorState.workflow.workflowInstanceEntityId,
      externalTaskStepId: this.args.step.externalStepId,
      appId: this.app.id,
      instructions: this.args.step.instructions,
      guidelines: this.args.step.guideline,
      onClose: () => {
        this.modalService.closeModal();
      },
    };

    this.modalService.openModal('react/workflows/fin-testing-suite-sheet', modalProps);
  }

  @action
  async maybeSaveRuleset() {
    await this.contentEditorService.saveRuleset.perform();
  }

  @action
  rulesetHasUnsavedChanges() {
    return this.contentEditorService.activeRulesetLink.hasUnsavedChanges;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/fin-custom/settings': typeof Settings;
  }
}
